import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { cx } from '../utils/cx';
import styles from './ScreenshotCarousel.module.css';

// Scroll-snap screenshot carousel used inside an <IPhone>. Autoplays every
// 3.4s (paused on hover), supports pointer drag-to-swipe on desktop, and
// auto-detects image orientation vs. the frame orientation (rotates a
// landscape image inside a portrait frame, or letterboxes a portrait image
// inside a landscape frame).
const ScreenshotCarousel = forwardRef(function ScreenshotCarousel(
  { screenshots, name, frameLand = false, onIndexChange, interval = 3400, delay = 0, dots = true },
  ref
) {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [fixes, setFixes] = useState({}); // { [i]: 'rot' | 'fit' }
  const hoveringRef = useRef(false);
  const draggingRef = useRef(false);
  const n = screenshots.length;

  const goTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const next = ((i % n) + n) % n;
    track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' });
  };

  useImperativeHandle(ref, () => ({ goTo }));

  useEffect(() => {
    onIndexChange && onIndexChange(index);
  }, [index, onIndexChange]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || n === 0) return;

    const sync = () => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      setIndex(i);
    };
    track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });

    let timer;
    let delayId;
    const restart = () => {
      clearInterval(timer);
      timer = setInterval(() => {
        if (!document.body.contains(track)) {
          clearInterval(timer);
          return;
        }
        if (!hoveringRef.current && !draggingRef.current) {
          const i = Math.round(track.scrollLeft / track.clientWidth);
          goTo(i + 1);
        }
      }, interval);
    };
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      delayId = setTimeout(restart, delay);
    }

    let sx = 0;
    let sl = 0;
    const down = (e) => {
      if (e.pointerType !== 'mouse') return;
      draggingRef.current = true;
      sx = e.clientX;
      sl = track.scrollLeft;
      track.style.scrollSnapType = 'none';
      track.setPointerCapture(e.pointerId);
    };
    const move = (e) => {
      if (!draggingRef.current) return;
      track.scrollLeft = sl - (e.clientX - sx);
    };
    const up = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      track.style.scrollSnapType = '';
      const d = track.scrollLeft - sl;
      const base = Math.round(sl / track.clientWidth);
      goTo(base + (Math.abs(d) > 40 ? Math.sign(d) : 0));
    };
    track.addEventListener('pointerdown', down);
    track.addEventListener('pointermove', move);
    track.addEventListener('pointerup', up);
    track.addEventListener('pointercancel', up);

    return () => {
      clearInterval(timer);
      clearTimeout(delayId);
      track.removeEventListener('pointerdown', down);
      track.removeEventListener('pointermove', move);
      track.removeEventListener('pointerup', up);
      track.removeEventListener('pointercancel', up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, interval, delay]);

  const handleImgLoad = (i) => (e) => {
    const img = e.target;
    if (!img.naturalWidth) return;
    const imgLand = img.naturalWidth > img.naturalHeight;
    if (imgLand && !frameLand) {
      const slide = img.parentElement;
      const ar = (slide.clientHeight / slide.clientWidth).toFixed(4);
      slide.style.setProperty('--ar', ar);
      setFixes((f) => ({ ...f, [i]: 'rot' }));
    } else if (!imgLand && frameLand) {
      setFixes((f) => ({ ...f, [i]: 'fit' }));
    }
  };

  return (
    <>
      <div
        ref={trackRef}
        className={styles.track}
        onMouseEnter={() => (hoveringRef.current = true)}
        onMouseLeave={() => (hoveringRef.current = false)}
      >
        {screenshots.map((src, i) => (
          <div
            key={src}
            className={cx(styles.slide, fixes[i] === 'rot' && styles.rot, fixes[i] === 'fit' && styles.fit)}
            style={{ '--src': `url('${src}')` }}
          >
            <img
              src={src}
              alt={`${name} screenshot ${i + 1}`}
              loading={i ? 'lazy' : undefined}
              draggable={false}
              onLoad={handleImgLoad(i)}
            />
          </div>
        ))}
      </div>
      {dots && (
        <div className={styles.dots}>
          {screenshots.map((_, i) => (
            <button
              key={i}
              aria-label={`Screenshot ${i + 1}`}
              className={cx(i === index && styles.on)}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </>
  );
});

export default ScreenshotCarousel;
